import React from "react"
import Tooltip from "@/components/ui/tooltip"
import { useOverflow } from "@/hooks/useOverflow"

interface TableCellProps {
  cellContent: React.ReactNode
  className?: string
  style?: React.CSSProperties
  colIndex: number
  selectable?: boolean
  isFirstColumn?: boolean
  isRowSelected?: boolean
  isRowSelectDisabled?: boolean
  onRowSelect?: () => void
}

const TableCell: React.FC<TableCellProps> = React.memo(({ 
  cellContent, 
  className, 
  style, 
  colIndex,
  selectable = false,
  isFirstColumn = false,
  isRowSelected = false,
  isRowSelectDisabled = false,
  onRowSelect
}) => {
  const { ref, isOverflowing } = useOverflow<HTMLDivElement>()

  // Extract text content for tooltip
  const extractTextContent = (element: React.ReactNode): string => {
    if (typeof element === "string") return element
    if (typeof element === "number") return element.toString()
    if (React.isValidElement<{ children?: React.ReactNode }>(element)) {
      if (element.props.children) {
        if (typeof element.props.children === "string") {
          return element.props.children
        }
        if (Array.isArray(element.props.children)) {
          return element.props.children
            .map((child: React.ReactNode) => extractTextContent(child))
            .join("")
        }
        return extractTextContent(element.props.children)
      }
    }
    return ""
  }

  const textContent = extractTextContent(cellContent)

  // Always render the same structure with ref attached
  // Only conditionally wrap with Tooltip when content is actually overflowing
  const cellInner = (
    <div ref={ref} className="truncate-text">
      {cellContent}
    </div>
  )

  // Only show tooltip if:
  // 1. Content is overflowing
  // 2. There is text content to show
  // 3. Content is not empty
  const shouldShowTooltip = isOverflowing && textContent && textContent.trim().length > 0

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation()
    if (!isRowSelectDisabled && onRowSelect) {
      onRowSelect()
    }
  }

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  return (
    <div
      className={`flex-cell ${className || ''}`}
      style={style}
    >
      {selectable && isFirstColumn && (
        <input
          checked={isRowSelected}
          disabled={isRowSelectDisabled}
          onChange={handleCheckboxChange}
          onClick={handleCheckboxClick}
          type="checkbox"
          className="row-checkbox"
        />
      )}
      {shouldShowTooltip ? (
        <Tooltip
          content={textContent}
          isFirstColumn={colIndex === 0}
          isTableCell={true}
          position="top"
          variant="default"
        >
          {cellInner}
        </Tooltip>
      ) : (
        cellInner
      )}
    </div>
  )
})

TableCell.displayName = 'TableCell'

export default TableCell

